import { z } from "zod";
/** AI proposal only: no canonical Requirement IDs, grants, executable actions, URLs or code. */
const Label=z.string().trim().min(1).max(500);
const Key=z.string().regex(/^[a-z][a-z0-9_]{0,47}$/);
const Base={id:Key,prompt:Label,required:z.boolean()};
// Literal kind tags make branches disjoint; union emits provider-compatible anyOf without weakening validation.
export const PreparationQuestion=z.union([
 z.object({...Base,kind:z.literal("TEXT"),maxLength:z.number().int().min(1).max(2000)}).strict(),
 z.object({...Base,kind:z.literal("SINGLE_CHOICE"),options:z.array(z.object({id:Key,label:Label}).strict()).min(2).max(10)
  .refine(values=>new Set(values.map(v=>v.id)).size===values.length,"DUPLICATE_OPTION")}).strict(),
 z.object({...Base,kind:z.literal("NUMBER"),min:z.number().finite(),max:z.number().finite(),unit:Label.nullable()}).strict()
  .refine(q=>q.min<=q.max,"INVALID_RANGE"),
]);
export const PreparationDraft=z.object({schemaVersion:z.literal(1),objective:z.string().trim().min(1).max(2000),
 outputs:z.array(Label).min(1).max(12),criteria:z.array(Label).min(1).max(12),constraints:z.array(Label).max(12),
 questions:z.array(PreparationQuestion).max(12).refine(q=>new Set(q.map(v=>v.id)).size===q.length,"DUPLICATE_QUESTION"),
}).strict().refine(v=>new TextEncoder().encode(JSON.stringify(v)).byteLength<=24000,"DRAFT_TOO_LARGE");
export type PreparationDraft=z.infer<typeof PreparationDraft>;

export const PreparationAnswer=z.discriminatedUnion("kind",[
 z.object({questionId:Key,kind:z.literal("TEXT"),value:z.string().trim().min(1).max(2000)}).strict(),
 z.object({questionId:Key,kind:z.literal("SINGLE_CHOICE"),optionId:Key}).strict(),
 z.object({questionId:Key,kind:z.literal("NUMBER"),value:z.number().finite()}).strict(),
]);
export const PreparationAnswers=z.object({sourceMessageId:z.uuid(),answers:z.array(PreparationAnswer).min(1).max(12)
 .refine(a=>new Set(a.map(v=>v.questionId)).size===a.length,"DUPLICATE_ANSWER")}).strict();
export type PreparationAnswers=z.infer<typeof PreparationAnswers>;
/** Current immutable source message and conversation version must be checked by the owner first. */
export function preparationAnswersMatch(draft:PreparationDraft,input:PreparationAnswers):boolean{
 const supplied=new Map(input.answers.map(a=>[a.questionId,a]));
 if(draft.questions.some(q=>q.required&&!supplied.has(q.id)))return false;
 return input.answers.every(a=>{const q=draft.questions.find(q=>q.id===a.questionId);if(!q||q.kind!==a.kind)return false;
  if(q.kind==="TEXT"&&a.kind==="TEXT")return a.value.length<=q.maxLength;
  if(q.kind==="NUMBER"&&a.kind==="NUMBER")return a.value>=q.min&&a.value<=q.max;
  return q.kind==="SINGLE_CHOICE"&&a.kind==="SINGLE_CHOICE"&&q.options.some(o=>o.id===a.optionId);
 });
}
